/**
 * ============================================================================
 * usePayoutMutations - PAYOUT MUTATION HOOKS
 * ============================================================================
 *
 * Handles all payout-related mutations with proper TanStack Query integration:
 * - Request payout with cache invalidation
 * - Optimistic updates for real-time UI feedback
 * - Automatic refetch of related queries
 *
 * ============================================================================
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/api';

interface PayoutRequestResult {
  success: boolean;
  message: string;
  data?: {
    payout_id: number;
    amount: number;
    status: string;
    transfer_reference: string;
    estimated_arrival: string;
  };
}

/**
 * Hook to request payout for a venue
 *
 * Automatically invalidates earnings cache to trigger refetch
 * This ensures balance updates are reflected immediately
 *
 * @example
 * ```typescript
 * const { mutate, isPending } = usePayoutRequest();
 *
 * const handlePayout = () => {
 *   mutate(venueId, {
 *     onSuccess: (data) => {
 *       console.log('Payout requested:', data);
 *       setShowSuccessModal(true);
 *     },
 *     onError: (error) => {
 *       alert(error.message);
 *     }
 *   });
 * };
 * ```
 */
export function usePayoutRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (venueId: number): Promise<PayoutRequestResult> => {
      return vendorApi.requestPayout(venueId);
    },

    onSuccess: (_data, venueId) => {
      // Invalidate earnings query to refetch fresh balance data
      queryClient.invalidateQueries({
        queryKey: ['earnings', venueId],
        refetchType: 'all'
      });

      // Invalidate payout history to show new payout
      queryClient.invalidateQueries({
        queryKey: ['payout-history', venueId],
        refetchType: 'all'
      });

      // Invalidate held bookings as they may have changed
      queryClient.invalidateQueries({
        queryKey: ['held-bookings', venueId],
        refetchType: 'all'
      });
    },

    onError: (error: Error) => {
      console.error('[PAYOUT] Payout request failed:', error.message);
    },
  });
}
